import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  CircularProgress,
  Alert,
  Rating,
  Chip,
  Pagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar
} from '@mui/material'
import {
  Favorite as FavoriteIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Add as AddIcon
} from '@mui/icons-material'
import {
  useGetWishlistQuery,
  useRemoveFromWishlistMutation,
  WishlistItem
} from '@store/api/wishlistApi'
import WishlistAddDialog from '@components/wishlist/WishlistAddDialog'
import PageHeader from '@components/layout/PageHeader'
import { APP_SNACKBAR_CONTENT_SX, useAppSnackbarAnchorSx } from '@components/layout/appSnackbar'
import { trackFeature } from '@utils/trackFeature'

interface WishlistProps {
  /** When true, omit page-level Container/padding so Profile (and similar) can embed this cleanly. */
  embedded?: boolean
}

const Wishlist: React.FC<WishlistProps> = ({ embedded = false }) => {
  const navigate = useNavigate()
  const snackbarAnchorSx = useAppSnackbarAnchorSx()
  const [page, setPage] = useState(1)
  const [addOpen, setAddOpen] = useState(false)
  const [removeTarget, setRemoveTarget] = useState<WishlistItem | null>(null)
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success'
  })

  const { data, isLoading, error } = useGetWishlistQuery({ page, limit: 12 })
  const [removeFromWishlist, { isLoading: isRemoving }] = useRemoveFromWishlistMutation()

  const handleRemove = async () => {
    if (!removeTarget) return
    try {
      await removeFromWishlist(removeTarget.id).unwrap()
      setPage(1)
      setSnackbar({ open: true, message: 'Removed from wishlist', severity: 'success' })
    } catch {
      setSnackbar({ open: true, message: 'Failed to remove from wishlist', severity: 'error' })
    } finally {
      setRemoveTarget(null)
    }
  }

  const content = (() => {
    if (isLoading) {
      return (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight={embedded ? 200 : '50vh'}>
          <CircularProgress />
        </Box>
      )
    }

    if (error) {
      return <Alert severity="error">Failed to load wishlist. Please try again.</Alert>
    }

    const handleOverflow = (action: string) => {
      trackFeature('overflow_action', { action })
      if (action === 'find') navigate('/app/database')
      if (action === 'add') setAddOpen(true)
    }

    return (
      <>
        {embedded ? (
          <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
            <Typography variant="h6" component="h2">
              Wishlist
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', ml: 'auto' }}>
              <Button
                variant="outlined"
                size="small"
                startIcon={<SearchIcon />}
                onClick={() => handleOverflow('find')}
              >
                Browse Database
              </Button>
              <Button
                variant="contained"
                size="small"
                startIcon={<AddIcon />}
                onClick={() => handleOverflow('add')}
              >
                Add Cigar
              </Button>
            </Box>
          </Box>
        ) : (
          <PageHeader
            title="Wishlist"
            primaryAction={{
              label: 'Add cigar',
              icon: <AddIcon />,
              onClick: () => handleOverflow('add'),
            }}
            overflowItems={[
              {
                id: 'find',
                label: 'Browse database',
                icon: <SearchIcon fontSize="small" />,
                onClick: () => handleOverflow('find'),
              },
            ]}
          />
        )}

        {!data?.items.length ? (
          <Box sx={{ textAlign: 'center', py: embedded ? 4 : 8 }}>
            <FavoriteIcon sx={{ fontSize: embedded ? 48 : 64, color: 'text.disabled', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              Your wishlist is empty
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Browse the cigar database and add cigars you want to try
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center', flexWrap: 'wrap' }}>
              <Button
                variant="outlined"
                startIcon={<SearchIcon />}
                onClick={() => {
                  trackFeature('empty_state_cta', { action: 'browse_database' })
                  navigate('/app/database')
                }}
              >
                Browse Cigars
              </Button>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => {
                  trackFeature('empty_state_cta', { action: 'add_wishlist' })
                  setAddOpen(true)
                }}
              >
                Add Cigar
              </Button>
            </Box>
          </Box>
        ) : (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {data.pagination.totalCount} cigar{data.pagination.totalCount !== 1 ? 's' : ''} on your wishlist
            </Typography>

            <Grid container spacing={3}>
              {data.items.map((item) => {
                const cigar = item.communityCigar
                const stats = cigar.cigarStatistics?.[0]
                return (
                  <Grid item xs={12} sm={6} md={4} key={item.id}>
                    <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                      <CardContent sx={{ flexGrow: 1 }}>
                        <Typography variant="h6" gutterBottom noWrap>
                          {cigar.name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          {cigar.brand?.name ?? 'Unknown Brand'}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1 }}>
                          {cigar.strengthLevel && (
                            <Chip label={cigar.strengthLevel} size="small" variant="outlined" />
                          )}
                          {cigar.vitola && (
                            <Chip label={cigar.vitola.name} size="small" variant="outlined" />
                          )}
                        </Box>
                        {stats && stats.averageRating > 0 && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Rating value={stats.averageRating} precision={0.5} readOnly size="small" />
                            <Typography variant="caption" color="text.secondary">
                              ({stats.totalReviews})
                            </Typography>
                          </Box>
                        )}
                        {cigar.averagePrice && (
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                            ~${Number(cigar.averagePrice).toFixed(2)}
                          </Typography>
                        )}
                      </CardContent>
                      <CardActions sx={{ justifyContent: 'space-between', px: 2, pb: 2 }}>
                        <Button
                          size="small"
                          onClick={() => navigate(`/app/database/cigars/${cigar.id}`)}
                        >
                          View Details
                        </Button>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <Button
                            size="small"
                            startIcon={<DeleteIcon />}
                            onClick={() => setRemoveTarget(item)}
                            color="error"
                          >
                            Remove
                          </Button>
                        </Box>
                      </CardActions>
                    </Card>
                  </Grid>
                )
              })}
            </Grid>

            {data.pagination.totalPages > 1 && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                <Pagination
                  count={data.pagination.totalPages}
                  page={page}
                  onChange={(_e, value) => setPage(value)}
                  color="primary"
                />
              </Box>
            )}
          </>
        )}

        <WishlistAddDialog
          open={addOpen}
          onClose={() => setAddOpen(false)}
          onResult={(message, severity) => setSnackbar({ open: true, message, severity })}
        />

        <Dialog open={!!removeTarget} onClose={() => setRemoveTarget(null)}>
          <DialogTitle>Remove from Wishlist</DialogTitle>
          <DialogContent>
            <Typography>
              Remove <strong>{removeTarget?.communityCigar.name}</strong> from your wishlist?
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setRemoveTarget(null)}>Cancel</Button>
            <Button onClick={handleRemove} color="error" disabled={isRemoving}>
              Remove
            </Button>
          </DialogActions>
        </Dialog>

        <Snackbar
          open={snackbar.open}
          autoHideDuration={3000}
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          sx={snackbarAnchorSx}
        >
          <Alert
            onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
            severity={snackbar.severity}
            sx={APP_SNACKBAR_CONTENT_SX}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </>
    )
  })()

  if (embedded) {
    return <Box>{content}</Box>
  }

  return (
    <Box sx={{ maxWidth: 'lg', mx: 'auto' }}>
      {content}
    </Box>
  )
}

export default Wishlist
